'use client';
import { projectName } from '../../packages/plugin-shared/projectChoice.mjs';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { EnvironmentOperation } from '../../src/shared/wireContract';
import { elowenClient } from './elowenClient';
import { isEnvironmentOperation } from './environmentActions';
import { subscribePluginEvents } from './pluginEvents';
import { ENVIRONMENT_OPERATION_EVENT } from './useEnvironmentOperation';
import { QUERY_KEYS, activeConversations, useBrainSessions, useMe, usePluginPresent, useSystemUpdateStatus, useMemoryMaintenance, useProjects } from './queries';
import { updateDockOperations, updateOperationTitle, type UpdateDockOperation } from './operationDockUpdates';
import { useAcknowledgeMemoryMaintenance } from './mutations';
import { createDockRowReconciler } from './operationDockRows';
import { memoryDockOperations, memoryDreamingSummary, type MemoryDockOperation } from './operationDockMemory';
import { interpolate, useTranslation } from './i18n';
import { UpdateOperationProgressDialog } from '../components/ui/UpdateOperationProgressDialog';
import { EnvironmentDockProgressDialog } from '../components/ui/EnvironmentDockProgressDialog';
import { PluginOperationProgressDialog } from '../components/ui/PluginOperationProgressDialog';
import { PluginErrorBoundary } from '../components/plugin/PluginUiGuards';
import type { MemoryDreamingRunView, BrainSessionInfo } from './types';
import { parseTs } from './format';
import { useMobileViewport } from './useMobile';
import { usePluginDockOperations, pluginDockItemId, type LoadedPluginDockSource, type PluginDockItem } from './operationDockPlugins';

export interface DockChat extends Pick<BrainSessionInfo, 'icon' | 'programStatus' | 'backgroundWorking' | 'activity'> {
  id: string;
  title: string;
  state: 'working' | 'background' | 'question' | 'permission' | 'done' | 'failed';
  detail: string;
  canStop: boolean;
  expiresAt: number | null;
}
/** Each source supplies presentation; the shell owns only shared controls and focus restoration. */
interface DockPresentation {
  title: string;
  statusLabel?: string;
  renderProgress: (props: {
    onClose: () => void; onClosed: () => void; successDelayMs: number;
    onAccepted: (id: string) => void; acknowledge: (id: string) => Promise<void>;
  }) => ReactNode;
}
interface DreamDockOperation {
  kind: 'memory-dreaming'; id: string; status: 'running' | 'succeeded' | 'failed';
  percent: number | null; run: MemoryDreamingRunView;
}
export type DockOperation = ((EnvironmentOperation & { kind: 'environment' }) | UpdateDockOperation | MemoryDockOperation | DreamDockOperation | PluginDockItem) & DockPresentation;
const CHAT_RESULT_LIFETIME = 24 * 60 * 60 * 1000;
const EMPTY_SESSIONS: BrainSessionInfo[] = [];
const EMPTY_PLUGIN_SOURCES: LoadedPluginDockSource[] = [];
const CHAT_PRIORITY: Record<DockChat['state'], number> = { question: 0, permission: 0, failed: 1, working: 2, background: 2, done: 3 };

function chatRows(sessions: BrainSessionInfo[], visibleSessionId: string | null, now: number): DockChat[] {
  return sessions.flatMap<DockChat>(session => {
    const activity = session.activity;
    if (!activity || activity.automation !== null || session.id === visibleSessionId) return [];
    const blocked = session.programStatus.state === 'blocked';
    const busy = blocked || activity.state === 'working' || session.backgroundWorking;
    const settled = activity.state === 'done' || activity.state === 'failed';
    if (!busy && !(settled && activity.unread)) return [];
    const expiresAt = !busy && settled ? parseTs(activity.at)! + CHAT_RESULT_LIFETIME : null;
    if (expiresAt !== null && expiresAt <= now) return [];
    const state: DockChat['state'] = blocked ? session.programStatus.kind === 'permission' ? 'permission' : 'question'
      : session.backgroundWorking ? 'background' : activity.state === 'working' ? 'working'
        : activity.state === 'failed' ? 'failed' : 'done';
    return [{ id: session.id, title: session.title, icon: session.icon, programStatus: session.programStatus,
      backgroundWorking: session.backgroundWorking, activity, state, detail: activity.detail, canStop: busy && session.canStop, expiresAt }];
  }).sort((a, b) => CHAT_PRIORITY[a.state] - CHAT_PRIORITY[b.state]);
}
interface OperationDockState {
  /** Only environment operations, for project busy states in plugin bundles. */
  operations: EnvironmentOperation[];
  items: DockOperation[];
  /** Own hand-started conversations needing attention, for the advisor launcher's menu. */
  conversations: DockChat[];
  openId: string | null;
  /** Track a hand-started operation in the minimized dock. `plugin` seeds that plugin's own source by id;
   *  `{ show: true }` then opens that source's view straight away, for a plugin whose work IS a window
   *  (an opened terminal) rather than a progress bar the reader can wait out in the dock. */
  open: (operationId: string, plugin?: string, options?: { show?: boolean }) => Promise<void> | void;
  /** Shell-only inspection action; plugin callers keep using open. */
  showProgress: (operationId: string) => void;
  minimize: () => void;
  acknowledge: (operationId: string) => Promise<void>;
}
export const OperationDockContext = createContext<OperationDockState | null>(null);

/** The server list and pushed durable rows are the only facts. The provider holds only the open view;
 * reload and reconnect seed the same list, including unseen outcomes and their acknowledgement. */
export function OperationDockProvider({ children, visibleSessionId = null, pluginSources = EMPTY_PLUGIN_SOURCES, resolvePluginSource, renderMemoryProgress }: {
  renderMemoryProgress: (operation: MemoryDockOperation, props: Parameters<DockPresentation['renderProgress']>[0]) => ReactNode;
  children: ReactNode; visibleSessionId?: string | null; pluginSources?: LoadedPluginDockSource[];
  resolvePluginSource?: (plugin: string) => Promise<LoadedPluginDockSource>;
}) {
  const qc = useQueryClient();
  const me = useMe();
  const userId = me.data?.user?.id;
  const sandbox = usePluginPresent('sandbox');
  const admin = me.data?.user?.is_admin === true;
  const updateStatus = useSystemUpdateStatus(admin);
  const sessions = useBrainSessions();
  const mobile = useMobileViewport();
  const plugins = usePluginDockOperations(pluginSources, userId, mobile);
  const pluginControl = useRef({ plugins, resolvePluginSource });
  pluginControl.current = { plugins, resolvePluginSource };
  const dreams = useQuery({
    queryKey: [...QUERY_KEYS.memoryDreaming, 'operations', userId],
    queryFn: elowenClient.memoryDreamingOperations,
    enabled: userId !== undefined,
    refetchOnMount: 'always',
  });
  const maintenance = useMemoryMaintenance(admin && mobile === false);
  const acknowledgeMemoryMaintenance = useAcknowledgeMemoryMaintenance();
  const projects = useProjects();
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now);
  const [openId, setOpenId] = useState<string | null>(null);
  // A plugin window opened right after its row was tracked: the row reaches `items` through the query
  // cache in the same commit, so the guard below must not read that first render as "the row is gone".
  const pendingShow = useRef<string | null>(null);
  const key = useMemo(() => [...QUERY_KEYS.environmentOperations, userId], [userId]);
  const reconciliation = useMemo(() => createDockRowReconciler<EnvironmentOperation>(), []);
  const query = useQuery({
    queryKey: [...QUERY_KEYS.environmentOperations, userId],
    enabled: sandbox && userId !== undefined,
    staleTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    // This bounded list seeds the shared account cache, not an individual observer. Complete it across
    // observer teardown so remounting the shell reuses the read rather than aborting and duplicating it.
    queryFn: () => reconciliation.read(key, userId, () => elowenClient.environmentOperations()),
  });
  useEffect(() => {
    if (!sandbox || userId === undefined) return;
    return subscribePluginEvents(event => {
      if (event.plugin !== 'sandbox' || event.kind !== ENVIRONMENT_OPERATION_EVENT || event.projectId === null) return;
      const operation = (event.data as { operation?: unknown } | null)?.operation;
      if (!isEnvironmentOperation(operation)) {
        console.error('Malformed environment operation frame');
        return;
      }
      if (operation.accountUserId !== userId || operation.initiator !== 'ui') return;
      qc.setQueryData<EnvironmentOperation[]>(key, rows => reconciliation.put(key, userId, rows ?? [], operation));
    });
  }, [qc, key, sandbox, userId, reconciliation]);

  const operations = useMemo(() => sandbox ? query.data ?? [] : [], [sandbox, query.data]);
  const conversations = useMemo(() => chatRows(activeConversations(sessions.data ?? EMPTY_SESSIONS), visibleSessionId, Math.max(now, Date.now())),
    [sessions.data, visibleSessionId, now]);
  useEffect(() => {
    const nextExpiry = Math.min(...conversations.flatMap(row => row.expiresAt === null ? [] : [row.expiresAt]));
    if (!Number.isFinite(nextExpiry)) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(0, nextExpiry - Date.now()));
    return () => clearTimeout(timer);
  }, [conversations]);

  const items = useMemo<DockOperation[]>(() => [
    ...operations.map(operation => {
      const project = projects.data?.find(project => project.id === operation.projectId);
      const title = `${t.operationProgress.actions[operation.action.kind]} · ${project ? projectName(project) : String(operation.projectId)}`;
      return { ...operation, kind: 'environment' as const, title,
        renderProgress: (props: Parameters<DockPresentation['renderProgress']>[0]) =>
          <EnvironmentDockProgressDialog {...props} operation={operation} title={title}
            onAcknowledge={() => props.acknowledge(operation.id)} /> };
    }),
    ...(dreams.data ?? []).map(run => {
      const status = run.status === 'running' ? 'running' as const
        : run.status === 'completed' ? 'succeeded' as const : 'failed' as const;
      const title = t.accountMemory.dreaming.operationTitle;
      const progressLabel = interpolate(t.accountMemory.dreaming.reviewing, { reviewed: run.reviewedCount, total: run.batchSize });
      const statusLabel = run.status === 'running' ? progressLabel : t.accountMemory.dreaming.status[run.status] + ' · ' + progressLabel;
      const operation = { title, status, statusLabel, detail: memoryDreamingSummary(run, t.accountMemory.dreaming.abortCause),
        percent: run.batchSize > 0 ? Math.min(100, run.reviewedCount / run.batchSize * 100) : null,
        historyUrl: `/account?cat=memory#accountMemory.dreaming.overview` };
      return { ...operation, id: 'dream:' + run.id, kind: 'memory-dreaming' as const, run,
        renderProgress: (props: Parameters<DockPresentation['renderProgress']>[0]) =>
          <PluginOperationProgressDialog operation={operation} onClose={props.onClose} onClosed={props.onClosed}
            onHistory={async () => {
              if (status !== 'running') await props.acknowledge('dream:' + run.id);
              window.location.assign(operation.historyUrl);
            }} /> };
    }),
    ...plugins.items.map(operation => ({
      ...operation,
      renderProgress: (props: Parameters<DockPresentation['renderProgress']>[0]) => {
        // A source that contributes a view draws its own window: the row it inspects is the plugin's own
        // (`operationId`), while `id` carries the host's namespaced key the dock is keyed on. `onClose`
        // is the window's MINIMIZE — a window keeps its own way to end the work it shows.
        if (operation.view) {
          const View = operation.view;
          return <PluginErrorBoundary key={operation.id} notice={t.pluginUi.crashed}>
            <View operation={{ ...operation, id: operation.operationId }} onClose={props.onClose}
              onClosed={props.onClosed} acknowledge={id => props.acknowledge(pluginDockItemId(operation.plugin, id))} />
          </PluginErrorBoundary>;
        }
        return <PluginOperationProgressDialog operation={operation} onClose={props.onClose} onClosed={props.onClosed}
          onHistory={async () => {
            if (operation.status === 'succeeded' || operation.status === 'failed') await props.acknowledge(operation.id);
            window.location.assign(operation.historyUrl);
          }} />;
      },
    })),
    ...(admin && userId !== undefined ? updateDockOperations(updateStatus.data, userId)
      .filter(operation => mobile === false || (operation.op !== 'install' && operation.op !== 'repair'))
      .map(operation => ({ ...operation, title: updateOperationTitle(operation, t.operationDock),
        renderProgress: (props: Parameters<DockPresentation['renderProgress']>[0]) =>
          <UpdateOperationProgressDialog {...props} operation={operation} /> })) : []),
    ...(admin && mobile === false ? memoryDockOperations(maintenance.data).map(operation => ({
      ...operation, title: operation.job.operation === 'reindex' ? t.memory.maintenanceReindex : t.memory.maintenanceRecategorize,
      renderProgress: (props: Parameters<DockPresentation['renderProgress']>[0]) =>
        renderMemoryProgress(operation, props),
    })) : []),
  ], [dreams.data, mobile, operations, plugins.items, updateStatus.data, maintenance.data, userId, admin, projects.data, t, renderMemoryProgress]);
  useEffect(() => {
    if (openId === null) { pendingShow.current = null; return; }
    if (items.some(item => item.id === openId)) {
      if (pendingShow.current === openId) pendingShow.current = null;
      return;
    }
    if (pendingShow.current === openId) return;
    setOpenId(null);
  }, [items, openId]);
  const open = useCallback((operationId: string, plugin?: string, options?: { show?: boolean }) => {
    setOpenId(null);
    if (plugin !== undefined) {
      const control = pluginControl.current;
      if (!control.resolvePluginSource) throw new Error('Plugin dock sources are not mounted');
      const itemId = pluginDockItemId(plugin, operationId);
      return control.resolvePluginSource(plugin).then(source => control.plugins.track(source, operationId))
        .then(() => {
          if (!options?.show) return;
          pendingShow.current = itemId;
          setOpenId(itemId);
        });
    }
    const kind = items.find(item => item.id === operationId)?.kind ?? 'environment';
    if (kind === 'environment' && !qc.getQueryData<EnvironmentOperation[]>(key)?.some(operation => operation.id === operationId)) {
      void qc.invalidateQueries({ queryKey: [...QUERY_KEYS.environmentOperations, userId] });
    }
  }, [items, qc, key, userId]);
  const showProgress = useCallback((operationId: string) => {
    setOpenId(operationId);
  }, []);
  const acknowledge = useCallback(async (operationId: string) => {
    try {
      const item = items.find(item => item.id === operationId);
      if (item?.kind === 'plugin-operation') {
        await pluginControl.current.plugins.acknowledge(item);
      } else if (item?.kind === 'update') {
        await elowenClient.acknowledgeUpdateOperation(item.requestId);
        await qc.invalidateQueries({ queryKey: QUERY_KEYS.systemUpdate });
      } else if (item?.kind === 'memory-dreaming') {
        await elowenClient.acknowledgeMemoryDreaming(item.run.id);
        await qc.invalidateQueries({ queryKey: QUERY_KEYS.memoryDreaming });
      } else if (item?.kind === 'memory-maintenance') {
        await acknowledgeMemoryMaintenance.mutateAsync(item.job.id!);
      } else {
        const operation = await elowenClient.acknowledgeEnvironmentOperation(operationId);
        qc.setQueryData<EnvironmentOperation[]>(key, rows => reconciliation.put(key, userId, rows ?? [], operation));
      }
    } catch (error) {
      console.error('Operation acknowledgement failed', error);
      throw error;
    }
  }, [items, qc, key, userId, reconciliation, acknowledgeMemoryMaintenance]);
  const value = useMemo(() => ({
    operations, items, conversations, openId, open, showProgress, minimize: () => setOpenId(null), acknowledge,
  }), [operations, items, conversations, openId, open, showProgress, acknowledge]);
  return <OperationDockContext.Provider value={value}>{children}</OperationDockContext.Provider>;
}

export function useOperationDock(): OperationDockState {
  const dock = useContext(OperationDockContext);
  if (!dock) throw new Error('useOperationDock requires OperationDockProvider');
  return dock;
}
